import { useQuery } from "@apollo/client";
import {
  Box,
  Center,
  Divider,
  Loader,
  Popover,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { Fragment, useState } from "react";
import { useDebounce } from "use-debounce";

import {
  GetManyOrdersOfUserDocument,
  NullsOrder,
  SortOrder,
} from "/@/generated/graphql";
import { buildOrderSearchWhere } from "/@/shared/utils/orders/orderSearch";
import { OrderSearchResult } from "./OrderSearchResult";

const RESULTS_LIMIT = 5;
const DROPDOWN_MAX_HEIGHT = 260;

export const OrderSearch = () => {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);

  const { data, loading, error } = useQuery(GetManyOrdersOfUserDocument, {
    fetchPolicy: "network-only",
    skip: debouncedSearch.trim().length === 0,
    variables: {
      where: buildOrderSearchWhere(debouncedSearch),
      take: RESULTS_LIMIT,
      orderBy: [
        { confirmedAt: { nulls: NullsOrder.Last, sort: SortOrder.Desc } },
        { updatedAt: SortOrder.Desc },
      ],
    },
  });

  const orders = data?.getManyOrdersOfUser ?? [];
  const isPending = loading || search.trim() !== debouncedSearch.trim();

  return (
    <Popover
      opened={search.trim().length > 0}
      onDismiss={() => setSearch("")}
      width="target"
      position="bottom-start"
      shadow="md"
      radius="md"
    >
      <Popover.Target>
        <TextInput
          flex={1}
          miw={0}
          maw={360}
          value={search}
          onChange={event => setSearch(event.currentTarget.value)}
          leftSection={<IconSearch size={16} />}
          placeholder="Rechercher une référence ou un patient"
          aria-label="Rechercher une référence ou un patient"
        />
      </Popover.Target>

      <Popover.Dropdown p={0} style={{ overflow: "hidden" }}>
        <Box mah={DROPDOWN_MAX_HEIGHT} style={{ overflowY: "auto" }}>
          {isPending ? (
            <Center py="md">
              <Loader size="sm" />
            </Center>
          ) : error ? (
            <Text variant="caption" ta="center" py="md">
              La recherche n&apos;a pas abouti
            </Text>
          ) : orders.length === 0 ? (
            <Text variant="caption" ta="center" py="md">
              Aucun résultat
            </Text>
          ) : (
            <Stack gap={0}>
              {orders.map((order, index) => (
                <Fragment key={order.id}>
                  {index > 0 && <Divider color="ink.1" />}
                  <OrderSearchResult
                    order={order}
                    onSelect={() => setSearch("")}
                  />
                </Fragment>
              ))}
            </Stack>
          )}
        </Box>
      </Popover.Dropdown>
    </Popover>
  );
};
